import { useCallback } from "react";
import { getBaseUrl } from "@Front/Api/helpers/configHelpers";
import type { ApiError, ApiRequestOptions, ApiResponse } from "@Front/Hooks/useApiClientTypes";
import { buildRequestInit, handleRequestFailureAsNever } from "@Front/Hooks/useApiClientRequestCore";

type ExecuteParams = {
	url: string;
	init: RequestInit;
	method: string;
	endpoint: string;
	errorMessage?: string;
	logErrors?: boolean;
	background?: boolean;
};

type ExecuteRequestFn = <T = unknown>(params: ExecuteParams) => Promise<ApiResponse<T>>;

function resolveUseApiClientRequestUrl(baseUrl: string, endpoint: string): string {
	if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
		return endpoint;
	}
	if (endpoint.startsWith("/api/")) {
		try {
			return new URL(endpoint, `${getBaseUrl()}/`).href;
		} catch {
			return `${baseUrl}${endpoint}`;
		}
	}
	const rel = endpoint.replace(/^\/+/, "");
	if (rel.length > 0) {
		const root = getBaseUrl().replace(/\/$/, "");
		return `${root}/${rel}`;
	}
	return `${baseUrl}${endpoint}`;
}

function resolveEndpointForAuthContext(url: string, fallbackEndpoint: string): string {
	try {
		const parsed = new URL(url);
		return `${parsed.pathname}${parsed.search}`;
	} catch {
		return fallbackEndpoint;
	}
}

export function useApiSendRequest(
	baseUrl: string,
	getAccessToken: (endpoint?: string) => string | null,
	getHeaders: (requireAuth?: boolean, additionalHeaders?: HeadersInit, endpoint?: string) => Headers,
	executeRequest: ExecuteRequestFn,
): <T = unknown>(
	method: "GET" | "POST" | "PUT" | "DELETE",
	endpoint: string,
	body?: unknown,
	options?: ApiRequestOptions,
) => Promise<ApiResponse<T>> {
	return useCallback(
		async <T = unknown>(
			method: "GET" | "POST" | "PUT" | "DELETE",
			endpoint: string,
			body?: unknown,
			options?: ApiRequestOptions,
		): Promise<ApiResponse<T>> => {
			const { requireAuth = true, errorMessage, logErrors = true, background, ...fetchOptions } = options || {};
			const url = resolveUseApiClientRequestUrl(baseUrl, endpoint);
			const endpointForAuth = resolveEndpointForAuthContext(url, endpoint);

			if (requireAuth && !getAccessToken(endpointForAuth)) {
				throw new Error("Token non trouvé") as ApiError;
			}

			const headers = getHeaders(requireAuth, fetchOptions.headers, endpointForAuth);
			const init = buildRequestInit(method, body, headers, fetchOptions);

			try {
				return await executeRequest<T>({ url, init, method, endpoint: endpointForAuth, errorMessage, logErrors, background });
			} catch (error: unknown) {
				return await handleRequestFailureAsNever(error, method, endpointForAuth, logErrors);
			}
		},
		[baseUrl, getAccessToken, getHeaders, executeRequest],
	);
}
